import { createContext, useEffect, useMemo, useState } from "react";

import appStyles from "../App.module.css";
import "../Calendar.css";
import "../DatePicker.css";
import { addDays, convertDateToApiFormat } from "../app-constants";
import { useBreakpointClass } from "../common-components/CommonBreakPointClass";
import PortugueseQuoteApi from "./api/PortugueseQuoteApi";
import Step from "./component/Step";
import PPHeader from "./component/common/PPHeader";
import {
  IPackagesDTO,
  IPHostCountryDTO,
  IPortugueseInitialQuoteData,
  IPortuguesePurchasePathContext,
  IPQuoteForm,
  IQuoteResultDTO,
  IRegion,
} from "./config/interface";
import portugueseConstant, { productBasedDestinationCountry, regions } from "./config/portuguese-Constant";

export const PortuguesePurchasePathContext = createContext<IPortuguesePurchasePathContext | null>(null);

const initialQuote: IPortugueseInitialQuoteData = {
  host_country: null,
  host_country_state: "",
  currency: {
    label: "EUR",
    value: "EUR",
  },
  regions: [regions[0]],
  start_date: null,
  end_date: null,
  productId: portugueseConstant.productId,
  main_applicant: null,
  applicantData: null,
  packages: null,
  pkgAmounts: [],
  excess_fee: portugueseConstant.excessFee1,
  affiliate_id: portugueseConstant.affiliatedId,
};

const PortuguesePurchasePath = () => {
  const bpClass = useBreakpointClass();
  const totalSteps: number = 4;
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [quoteData, setQuoteData] = useState<IPortugueseInitialQuoteData>(() => initialQuote);

  const [quoteResult, setQuoteResult] = useState<IQuoteResultDTO | null>(null);
  const [order, setOrder] = useState<any>(null);
  const [loader, setLoader] = useState<"master_data" | "quote" | "packages" | "order" | "payment" | "policy" | null>(
    "master_data"
  );

  const [hostCountries, setHostCountries] = useState<IPHostCountryDTO[]>([]);

  const [packages, setPackages] = useState<IPackagesDTO[]>([]);

  const [currentTimeStamp, setCurrentTimeStamp] = useState<number>(new Date().getTime());

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimeStamp]);

  const init = async () => {
    try {
      const _quoteData: IPortugueseInitialQuoteData = { ...quoteData };

      // affiliated values always available either user pass or not
      const urlParams = new URLSearchParams(window.location.search);
      _quoteData.affiliate_id = urlParams.get("partner") || portugueseConstant.affiliatedId!;

      const hcResponse: any = await PortugueseQuoteApi.getHostCountries(quoteData.productId);
      const sortedHostCountries = (hcResponse?.data || [])
        .filter((item: any) => !item.is_disabled)
        .sort((a: any, b: any) => a.name.localeCompare(b.name));

      setHostCountries(sortedHostCountries);

      if (hcResponse?.data?.length === 1) {
        _quoteData.host_country = hcResponse?.data[0];
      }

      setQuoteData(() => _quoteData);
    } catch (e) {
      console.log(e);
    } finally {
      setLoader(null);
    }
  };

  const updateCountryAndDestination = async (values: IPQuoteForm, region: IRegion) => {
    try {
      const _quoteData: IPortugueseInitialQuoteData = { ...quoteData };
      _quoteData.productId = region.value;
      const hcResponse: any = await PortugueseQuoteApi.getHostCountries(region.value);

      const hostCountries = (hcResponse?.data || [])
        .filter((item: any) => !item.is_disabled)
        .sort((a: any, b: any) => a.name.localeCompare(b.name));

      if (hostCountries?.length === 1 && values.host_country?.code !== hostCountries[0].code) {
        _quoteData.host_country = hostCountries[0];
      } else if (values?.host_country && hostCountries?.length >= 1) {
        const isHostCountryPresent = hostCountries.some(
          (_hostCountry: any) => _hostCountry?.code === values?.host_country?.code
        );

        if (isHostCountryPresent) {
          setHostCountries(() => hostCountries || []);
          setQuoteData(() => _quoteData);
          return;
        } else {
          _quoteData.host_country = null;
        }
      }

      _quoteData.currency = values.currency;
      _quoteData.start_date = values.start_date;
      _quoteData.end_date = values.end_date;
      _quoteData.regions = [region];
      _quoteData.main_applicant = values.main_applicant;
      setHostCountries(() => hostCountries || []);
      setQuoteData(() => _quoteData);
    } catch (e) {
      console.log(e);
    }
  };

  const getAllowedPackages = (_pkg: any, _quoteData: IPortugueseInitialQuoteData) => {
    let item: any = [];
    _pkg.forEach((_package: any) => {
      const _pkgFound = _package?.validations?.find(
        (elem: any) => elem.product_package_validation_type?.name === "Start date isn't within days"
      );

      if (_pkgFound) {
        const validationDate = addDays(new Date(), parseInt(_pkgFound.value));
        const startDate = new Date(_quoteData.start_date || new Date());

        if (startDate > validationDate) {
          item.push(_package);
        }
      } else {
        item.push(_package);
      }
    });
    return item;
  };

  const getPackages = async (_quoteData: IPortugueseInitialQuoteData) => {
    const pkgResponse: any = await PortugueseQuoteApi.getHostCountryPackages(
      _quoteData.productId,
      _quoteData.host_country?.code || null
    );

    if (pkgResponse.error) {
      throw pkgResponse;
    }
    _quoteData.packages = pkgResponse?.data.find((_pkg: any) => _pkg.alias === "base_package");
    const _allowedPackages = getAllowedPackages(pkgResponse?.data, _quoteData);

    const data: any = {
      start_date: convertDateToApiFormat(_quoteData.start_date),
      end_date: convertDateToApiFormat(_quoteData.end_date),
      affiliate: quoteData?.affiliate_id,
      applicants: [
        {
          is_main: true,
          dob: convertDateToApiFormat(_quoteData.main_applicant),
          trip_cost: 1000,
        },
      ],
      currency: _quoteData.currency?.value,
      deductibles: {
        excess_fee: _quoteData.excess_fee,
      },
      deposit_date: convertDateToApiFormat(new Date()),
      discount: {
        percentage: 0,
      },
      host_country: _quoteData.host_country?.code,
      product: Number(_quoteData.productId),
      packages: _allowedPackages.map((_pkg: any) => _pkg.alias),
      destination_groups: [productBasedDestinationCountry[_quoteData.productId]],
    };
    if (_quoteData?.host_country?.states && _quoteData?.host_country?.states?.length > 0) {
      data.host_country_state = _quoteData.host_country?.states[0]?.code;
    }

    const pkgAmt = await PortugueseQuoteApi.packageAmount(data);
    _quoteData.pkgAmounts = pkgAmt?.data?.rates;

    return _allowedPackages;
  };

  const getQuote = async (_quoteData: IPortugueseInitialQuoteData) => {
    const apiData: any = {
      start_date: convertDateToApiFormat(_quoteData.start_date),
      end_date: convertDateToApiFormat(_quoteData.end_date),
      deposit_date: convertDateToApiFormat(new Date()),
      product: Number(_quoteData.productId),
      host_country: _quoteData.host_country?.code,
      currency: _quoteData.currency?.value,
      packages: [_quoteData.packages.alias],
      applicants: [
        {
          is_main: true,
          dob: convertDateToApiFormat(_quoteData.main_applicant),
          trip_cost: 1000,
        },
      ],

      deductibles: {
        excess_fee: _quoteData.excess_fee,
      },

      destination_groups: [productBasedDestinationCountry[_quoteData.productId]],
      affiliate: quoteData?.affiliate_id,
    };
    if (_quoteData?.host_country?.states && _quoteData?.host_country?.states?.length > 0) {
      apiData.host_country_state = _quoteData.host_country?.states[0]?.code;
    }

    const quoteResponse: any = await PortugueseQuoteApi.getQuickQuote(apiData);

    if (quoteResponse.error) {
      throw quoteResponse;
    }

    quoteResponse.data.details = (quoteResponse?.data?.details || []).map((quote: any) => {
      quote.expanded = false;
      return quote;
    });
    return quoteResponse?.data || null;
  };

  const reset = () => {
    setCurrentStep(0);
    setHostCountries([]);
    setQuoteData(() => initialQuote);
    setPackages([]);
    setQuoteResult(null);
    setOrder(null);
    setCurrentTimeStamp(new Date().getTime());
  };

  const contextValue = useMemo(
    () => ({
      totalSteps,
      currentStep,
      hostCountries,
      packages,
      setPackages,
      quoteData,
      setQuoteData,
      quoteResult,
      setQuoteResult,
      order,
      setOrder,
      loader,
      setLoader,
      getPackages,
      updateCountryAndDestination,
      getQuote,
      reset,
      next: () => {
        if (currentStep < totalSteps) {
          setCurrentStep(currentStep + 1);
        }
      },
      previous: () => {
        if (currentStep > 0) {
          setCurrentStep(currentStep - 1);
        }
      },
    }),
    [currentStep, quoteData, loader, order, quoteResult, hostCountries]
  );

  return (
    <div className={`${appStyles.battlefacePlugin} ${bpClass} ${appStyles.ppContainerPX}`}>
      <PortuguesePurchasePathContext.Provider value={contextValue}>
        <PPHeader />
        <Step />
      </PortuguesePurchasePathContext.Provider>
    </div>
  );
};

export default PortuguesePurchasePath;
